import { useCallback, useRef } from "react";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { clearAppPersistence } from "~/shared/utils/persistence";

const AUTH_SESSION_EXPIRED_EVENT = "auth:session-expired";

/**
 * Detects if an error is due to expired/invalid token (401 or token-related message).
 */
export function isTokenError(err: unknown): boolean {
  if (err == null) return false;
  const msg = String(
    (err as { message?: string })?.message ??
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    ""
  ).toLowerCase();
  const status =
    (err as { response?: { status?: number } })?.response?.status ??
    (err as { statusCode?: number })?.statusCode;
  return (
    status === 401 ||
    msg.includes("invalid azure ad token") ||
    msg.includes("invalid token") ||
    msg.includes("token expired") ||
    msg.includes("token missing") ||
    msg.includes("session expired")
  );
}

/**
 * Detects if an error is a 502 Bad Gateway (server/proxy error).
 */
export function isServerError(err: unknown): boolean {
  if (err == null) return false;
  const status =
    (err as { response?: { status?: number } })?.response?.status ??
    (err as { statusCode?: number })?.statusCode;
  return status === 502;
}

/**
 * Detects if an error is retryable (token error OR 502 server error).
 */
export function isRetryableError(err: unknown): boolean {
  return isTokenError(err) || isServerError(err);
}

/**
 * Clears MSAL active account, wipes all persisted app state, and dispatches
 * session-expired so the app redirects to login.
 */
export function triggerSessionExpired(msalInstance: IPublicClientApplication | null): void {
  try {
    msalInstance?.setActiveAccount(null);
  } catch {
    /* ignore */
  }
  clearAppPersistence();
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
}

/**
 * Hook: shared retryable-error handling for list/load screens.
 * - On first retryable error (401 token / 502 gateway): schedules one retry.
 * - On second error (retry failed): triggers logout and redirect to login.
 */
export function useTokenErrorHandler(msalInstance: IPublicClientApplication | null) {
  const tokenErrorRetriedRef = useRef(false);

  const handleTokenError = useCallback(
    (
      err: unknown,
      retryLoad: () => void,
      allowReload: () => void
    ): boolean => {
      if (!isRetryableError(err)) return false;

      if (!tokenErrorRetriedRef.current) {
        tokenErrorRetriedRef.current = true;
        allowReload();
        setTimeout(retryLoad, 0);
        return true;
      }

      triggerSessionExpired(msalInstance);
      return true;
    },
    [msalInstance]
  );

  const resetTokenErrorRetry = useCallback(() => {
    tokenErrorRetriedRef.current = false;
  }, []);

  return { isTokenError, handleTokenError, resetTokenErrorRetry };
}

